import React, { ReactElement, SyntheticEvent, useState } from 'react';
import styles from './resetPasswordPage.module.scss';
import { Title } from '../../../components/title/Title';
import PasswordInput from '../../changePassword/passwordInput/PasswordInput';
import { TechnicalError } from '../../../components/technicalError/TechnicalError';
import { usePasswordResetVerifyToken } from '../hooks/useVerifyToken/useVerifyToken';
import { LoadableButton } from '@mo/loadable-button';
import Padlock from '../../../components/padlock/Padlock';
import { PageErrorBox } from '@mo/page-error-box';
import { PageError } from '../../../domain/PageError';
import CurrentPasswordInput from '../../changePassword/passwordInput/CurrentPasswordInput';
import { resetPassword } from '../../changePassword/changePasswordGateway';
import LinkExpiredPage from '../linkExpired/LinkExpiredPage';
import { BASE_PATH } from '../../../common/routes'; 
import PasswordUpdatedPage from '../../changePassword/passwordUpdated/PasswordUpdatedPage';
import {
    BLANK_CURRENT_PASSWORD,
    BLANK_OR_INVALID_PASSWORD,
    GENERIC_PASSWORD_ERROR,
    INVALID_CHARACTER,
    nonAsciiRegex,
    PASSWORD_TOO_LONG,
    passwordRegexp,
    PASSWORDS_IDENTICAL_ERROR,
} from '../../../common/passwordValidation/validation';
import {
    AccountLockedError,
    PageValidationErrors,
    PasswordValidationErrors,
    UnauthorisedError,
} from '../../../common/passwordValidation/errors';
import { ServiceUnavailableError } from '../../../common/errors';
import AccountLockedPage from '../../changePassword/accountLocked/AccountLockedPage';
import { useHistory } from 'react-router-dom';
import { BackButton } from '@mo/back-button';

export interface ResetPasswordPageProps {
    token: string;
    username: string;
    resumeUrl: string;
    resumeButtonText?: string;
    title?: string;
    instructionText?: string;
}

const blankValidationErrors = { currentPassword: [], newPassword: [] };

function ResetPasswordPage({
    username,
    token,
    resumeUrl,
    resumeButtonText = 'Return to Login page',
    title = 'Reset password',
    instructionText = `Please create a secure password to Sign in going forward.`,
}: ResetPasswordPageProps): ReactElement | null {
    const { loading, unauthorisedError } = usePasswordResetVerifyToken(token, username);
    const [accountLocked, setAccountLocked] = useState<boolean>(false);
    const [currentPassword, setCurrentPassword] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [technicalError, setTechnicalError] = useState<boolean>(false);
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string[] }>(blankValidationErrors);
    const [submissionLoading, setSubmissionLoading] = useState<boolean>(false);
    const [passwordChanged, setPasswordChanged] = useState<boolean>(false);
    const history = useHistory();

    const validatePasswords = (): void => {
        const currentPasswordValidationErrors = [];
        currentPassword.length === 0 && currentPasswordValidationErrors.push(BLANK_CURRENT_PASSWORD);

        const newPasswordValidationErrors = [];

        if (newPassword.length === 0) {
            newPasswordValidationErrors.push(BLANK_OR_INVALID_PASSWORD);
        } else if (nonAsciiRegex.test(newPassword)) {
            newPasswordValidationErrors.push(INVALID_CHARACTER);
        } else if (!passwordRegexp.test(newPassword)) {
            newPasswordValidationErrors.push(BLANK_OR_INVALID_PASSWORD);
        } else if (currentPassword === newPassword) {
            newPasswordValidationErrors.push(PASSWORDS_IDENTICAL_ERROR);
        } else if (newPassword.length > 64) {
            newPasswordValidationErrors.push(PASSWORD_TOO_LONG);
        }

        if (currentPasswordValidationErrors.concat(newPasswordValidationErrors).length > 0) {
            throw new PageValidationErrors({
                currentPassword: currentPasswordValidationErrors,
                newPassword: newPasswordValidationErrors,
            });
        }
    };

    const handleSubmit = async (event: SyntheticEvent): Promise<void> => {
        event.preventDefault();
        try {
            setValidationErrors(blankValidationErrors);
            validatePasswords();

            setSubmissionLoading(true);
            await resetPassword(username, currentPassword, newPassword);
            setSubmissionLoading(false);
            setPasswordChanged(true);
        } catch (error) {
            if (error instanceof PageValidationErrors) {
                return setValidationErrors(error.validationErrors);
            }

            setSubmissionLoading(false);

            if (error instanceof UnauthorisedError) {
                return setValidationErrors({ ...validationErrors, currentPassword: [error.message] });
            }

            if (error instanceof PasswordValidationErrors) {
                return setValidationErrors({ ...validationErrors, newPassword: error.validationErrors });
            }

            if (error instanceof AccountLockedError) {
                return setAccountLocked(true);
            }

            if (error instanceof ServiceUnavailableError) {
                setTechnicalError(true);
            }
        }
    };

    const mapPasswordValidationPageErrors = (validationErrors: string[], label: string): PageError[] => {
        return (
            validationErrors &&
            validationErrors.map(
                (validationError: string): PageError => {
                    return { label, message: validationError };
                }
            )
        );
    };

    const mapPasswordValidationInlineErrors = (validationErrors: string[]): string | undefined => {
        if (validationErrors.length > 1) return GENERIC_PASSWORD_ERROR;
        else if (validationErrors.length === 1) return validationErrors[0];
    };

    const errors = [
        ...mapPasswordValidationPageErrors(validationErrors.currentPassword, 'Current password'),
        ...mapPasswordValidationPageErrors(validationErrors.newPassword, 'New password'),
    ];

    const hasErrors = errors.length > 0;

    const goBack = (): void => {
        if (window.history.length <= 1) {
            history.replace(BASE_PATH);
        } else {
            history.go(-1);
        }
    };

    if (accountLocked) {
        return <AccountLockedPage />;
    }

    if (passwordChanged) {
        return <PasswordUpdatedPage resumeButtonText={resumeButtonText} resumeUrl={resumeUrl} />;
    }

    if (loading) {
        return null;
    }

    if (unauthorisedError) {
        return <LinkExpiredPage />;
    } else {

        return (
            <div className={styles.gridContainer}>
                <div className={styles.page}>
                    <div className={styles.form}>
                        <div className={styles.formHeader}>
                            <div className={hasErrors ? styles.formHeaderContentInError : styles.formHeaderContent}>
                                <BackButton onClick={goBack} text='Back' />
                                <PageErrorBox hasErrors={hasErrors} errors={errors} />
                                <Title title={title} />
                                <p className={styles.instructionText}>{instructionText}</p>
                            </div>
                        </div>
                        <div className={styles.formBody}>
                            <div className={styles.passwordEntryContainer}>
                                <div className={styles.passwordEntryForm}>
                                    <form onSubmit={handleSubmit}>
                                        <CurrentPasswordInput
                                            password={currentPassword}
                                            error={mapPasswordValidationInlineErrors(validationErrors.currentPassword)}
                                            onChange={(existingPassword: string): void => {
                                                setCurrentPassword(existingPassword);
                                            }}
                                        />
                                        <PasswordInput
                                            password={newPassword}
                                            error={mapPasswordValidationInlineErrors(validationErrors.newPassword)}
                                            onChange={(newPassword: string): void => {
                                                setNewPassword(newPassword);
                                            }}
                                        />
                                        {technicalError && (
                                            <TechnicalError>
                                                Sorry, we're having some technical difficulties. Please try again 
                                                in a few moments. If the problem persist please contact your team 
                                                at Manufacturer Relations <a href='mailto:manufacturerrelations@motabilityoperations.co.uk'>manufacturerrelations@motabilityoperations.co.uk </a> 
                                                or DFW and adaptations <a href='mailto:dfwandadaptation@motabilityoperations.co.uk'>dfwandadaptation@motabilityoperations.co.uk</a>.
                                            </TechnicalError>
                                        )}
                                        <LoadableButton
                                            loading={submissionLoading}
                                            loadingText='Saving'
                                            notLoadingText='Save'
                                            onClick={handleSubmit}
                                            className={styles.continueButton}
                                        />
                                        <LoadableButton
                                            loading={false}
                                            loadingText='Cancel'
                                            notLoadingText='Cancel'
                                            type={'button'}
                                            onClick={goBack}
                                            className={styles.cancelButton}
                                        />
                                    </form>
                                </div>
                                <Padlock className={styles.padlock} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default ResetPasswordPage;
